import { createServerFn } from "@tanstack/react-start"
import { summarize, type GoldOptionsSummary, type RawOption } from "@/lib/gold-options"

// CBOE delayed GLD option chain. Open interest settles once per session, so an
// hourly in-memory cache plus a durable last-good snapshot keeps provider load flat.

export type GoldOptionsDTO = { summary: GoldOptionsSummary | null; stale?: boolean; error?: string }

const SOURCE = "https://cdn.cboe.com/api/global/delayed_quotes/options/GLD.json"
const KEY = "gold-options-gld"
const TTL = 60 * 60 * 1000
let cache: { at: number; data: GoldOptionsDTO } | null = null

function yymmdd(d: Date) {
  return d.toISOString().slice(2, 10).replace(/-/g, "")
}

async function liveSpot(): Promise<number | null> {
  try {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server")
    const { data } = await supabaseAdmin.from("gold_price_cache").select("price").eq("id", 1).maybeSingle()
    if (data?.price) return Number(data.price)
  } catch (e) {
    console.warn("[gold-options] cached spot unavailable", e)
  }
  try {
    const { fetchGoldOHLC } = await import("@/server/twelve-data.server")
    const c = await fetchGoldOHLC("1day", 2)
    return c.at(-1)?.close ?? null
  } catch (e) {
    console.warn("[gold-options] spot unavailable", e)
    return null
  }
}

// Fresh instances read the stored summary (about 15KB) before the 3MB CBOE chain.
async function freshStored(): Promise<{ at: number; summary: GoldOptionsSummary } | null> {
  try {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server")
    const { data } = await supabaseAdmin.from("macro_data_snapshots").select("payload, updated_at").eq("key", KEY).maybeSingle()
    if (!data?.payload || !data.updated_at) return null
    const at = new Date(data.updated_at).getTime()
    return Date.now() - at < TTL ? { at, summary: data.payload as unknown as GoldOptionsSummary } : null
  } catch {
    return null
  }
}

export const getGoldOptions = createServerFn({ method: "GET" }).handler(async (): Promise<GoldOptionsDTO> => {
  if (cache && Date.now() - cache.at < TTL) return cache.data
  const stored = await freshStored()
  if (stored) {
    cache = { at: stored.at, data: { summary: stored.summary } }
    return cache.data
  }
  const { saveSnapshot, loadSnapshot } = await import("@/lib/macro-snapshot.server")
  try {
    const [res, spot] = await Promise.all([fetch(SOURCE, { headers: { "User-Agent": "Mozilla/5.0 XAUProAcademy" }, signal: AbortSignal.timeout(12000) }), liveSpot()])
    if (!res.ok) throw new Error(`CBOE ${res.status}`)
    const json = (await res.json()) as { data?: { options?: RawOption[]; current_price?: number; iv30?: number; last_trade_time?: string } }
    const d = json.data
    if (!d?.options?.length || !d.current_price) throw new Error("CBOE empty chain")
    const summary = summarize({
      options: d.options,
      gldPrice: d.current_price,
      iv30: d.iv30 ?? null,
      spot,
      asOf: d.last_trade_time ?? new Date().toISOString(),
      today: yymmdd(new Date()),
    })
    const data: GoldOptionsDTO = { summary }
    cache = { at: Date.now(), data }
    await saveSnapshot(KEY, summary)
    return data
  } catch (e) {
    console.error("[gold-options] live fetch failed", e)
    const last = await loadSnapshot<GoldOptionsSummary>(KEY)
    const data: GoldOptionsDTO = last ? { summary: last, stale: true } : { summary: null, error: "Options data temporarily unavailable." }
    cache = { at: Date.now() - TTL + 5 * 60 * 1000, data }
    return data
  }
})
